import { daysToMilliseconds } from '~core/plugins/helpers/time-conversion'
import { useLocalStorage } from '~/composables/use-local-storage'
import { LocalStorageKey } from '~/models/local-storage-key'

enum HHCareerEvent {
  Redirect = 'REDIRECT',
  Order = 'ORDER',
  Callback = 'CALLBACK',
  Paid = 'PAID',
  Rejected = 'REJECTED',
}

type HHCareerStorageItem = Record<
  number,
  { transactionId: string; expires: number }
>

const ENDPOINT = 'https://career.hh.ru/events'
const TRANSACTION_ID_TTL = daysToMilliseconds(2)

function extractTransactionId(): string | null {
  const route = useRoute()

  const transactionId = route.query.transaction_id

  // we don't know exact transaction ID length, but it's definitely longer than 10 characters
  return typeof transactionId === 'string' && transactionId.length > 10
    ? transactionId
    : null
}

function saveOfferTransactionId(offerId: number, transactionId: string): void {
  const store = useLocalStorage<HHCareerStorageItem>(LocalStorageKey.HHCareer)

  store.value ??= {}
  store.value[offerId] = {
    transactionId,
    expires: Date.now() + TRANSACTION_ID_TTL,
  }
}

function getOfferTransactionId(offerId: number): string | null {
  const store = useLocalStorage<HHCareerStorageItem>(LocalStorageKey.HHCareer)
  const item = store.value?.[offerId] ?? null

  if (item?.transactionId) {
    if (item.expires >= Date.now()) {
      return item.transactionId
    } else {
      delete store.value![offerId]
    }
  }

  return null
}

async function sendEvent(
  event: HHCareerEvent,
  offerId: number,
  offerName: string,
  params?: Record<string, string | number>
): Promise<void> {
  const transactionId = getOfferTransactionId(offerId)
  if (!transactionId) {
    return
  }

  params = {
    ...(params || {}),
    offer_id: offerId,
    offer_name: offerName,
    transaction_id: transactionId,
    adv_id: getAdvId(),
    action: event,
  }

  if (!params.adv_id) {
    console.log('[HH Career Debug] Send event: ', params)
    return
  }

  await $fetch(ENDPOINT, { retry: 0, params })
}

async function sendRedirectEvent(
  offerId: number,
  offerName: string
): Promise<void> {
  await sendEvent(HHCareerEvent.Redirect, offerId, offerName)
}

async function sendPaidEvent(
  offerId: number,
  offerName: string,
  amount: number,
  promocode: string | null = null
): Promise<void> {
  const params: any = {
    amount: amount.toFixed(2),
    commission: (amount * getCommissionMultiplier()).toFixed(2),
  }

  if (promocode) {
    params.promocode = promocode
  }

  await sendEvent(HHCareerEvent.Redirect, offerId, offerName, params)
}

function getAdvId(): number {
  return parseInt(useRuntimeConfig().public.hhCareerAdvId || '0')
}

function getCommissionMultiplier(): number {
  return parseFloat(useRuntimeConfig().public.hhCareerAdvId || '0')
}

export default defineNuxtPlugin(() => {
  return {
    provide: {
      hhCareer: {
        extractTransactionId,
        saveOfferTransactionId,
        getOfferTransactionId,
        sendRedirectEvent,
        sendPaidEvent,
      },
    },
  }
})
